/**
 * El orden por columna de las tablas de `imprenta`.
 *
 * Sus encabezados ciclan **asc → desc → sin orden**, y el tercer paso es el que
 * importa: sin él no hay forma de volver al orden natural sin recargar la
 * página. Estaba escrito inline en tres pantallas.
 */

export interface Orden {
  campo: string;
  direccion: "asc" | "desc";
}

/** El siguiente estado del ciclo al clickear `campo`. `null` es «sin orden». */
export function siguienteOrden(actual: Orden | null, campo: string): Orden | null {
  if (actual?.campo !== campo) return { campo, direccion: "asc" };
  return actual.direccion === "asc" ? { campo, direccion: "desc" } : null;
}

/**
 * Ordena una lista por el campo que pida `orden`, con las reglas de `imprenta`.
 *
 * Estaba escrita **una vez por pantalla** (`ordenarClientes`), y cada listado
 * nuevo la volvía a copiar. Las tres reglas que hay que no perder:
 *
 * 1. **Los vacíos van al final**, en las dos direcciones. Un cliente sin CUIT no
 *    es «el primero»: es el que no tiene el dato, y arriba tapa a los que sí.
 * 2. **Los números se comparan como números.** `"100"` antes que `"99"` es lo
 *    que pasa si se los compara como texto.
 * 3. **`localeCompare` con `es`**: sin eso la Ñ cae después de la Z.
 *
 * `valorDe` lo da la pantalla porque solo ella sabe qué columna mira qué campo.
 */
export function ordenarLista<T>(
  items: readonly T[],
  orden: Orden | null,
  valorDe: (item: T, campo: string) => string | number | null,
): T[] {
  if (!orden) return [...items];
  const signo = orden.direccion === "desc" ? -1 : 1;

  return [...items].sort((a, b) => {
    const x = valorDe(a, orden.campo);
    const y = valorDe(b, orden.campo);
    const xVacio = x === null || x === "";
    const yVacio = y === null || y === "";
    if (xVacio) return yVacio ? 0 : 1;
    if (yVacio) return -1;
    if (typeof x === "number" && typeof y === "number") return (x - y) * signo;
    return String(x).localeCompare(String(y), "es", { numeric: true }) * signo;
  });
}
